"use client";

import React, { useEffect, useRef, useMemo } from "react";
import { createNoise3D } from "simplex-noise";
import { useTheme } from "next-themes";

const TILE_WIDTH = 30;
const TILE_HEIGHT = 30;
const NAVBAR_HEIGHT = 70;

const config = {
    enablePerspective: true,
    perspective: 1000,
    rotationX: -75 * (Math.PI / 180),
    grainStrength: 0.33,
};

const fbm3D = (x, y, z, noise3D) => {
    let value = 0;
    let amplitude = 0.75;
    let frequency = 0.5;
    const octaves = 2;
    for (let i = 0; i < octaves; i++) {
        value += amplitude * noise3D(x * frequency, y * frequency, z * frequency);
        amplitude *= 0.5;
        frequency *= 2;
    }
    return value;
};

const loadShader = async (url) => {
    const response = await fetch(url);
    return await response.text();
};

const createShader = (gl, type, source) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error("Error compiling shader:", gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
    }

    return shader;
};

const createProgram = (gl, vertexShader, fragmentShader) => {
    if (!vertexShader || !fragmentShader) {
        console.error("Cannot create program with invalid shaders.");
        return null;
    }
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error("Error linking program:", gl.getProgramInfoLog(program));
        gl.deleteProgram(program);
        return null;
    }

    return program;
};

// --- Main Component ---
export default function Hero({ children }) {
    const canvasRef = useRef(null);
    const noiseCanvasRef = useRef(null);
    const animationFrameId = useRef(null);
    const noiseAnimationFrameId = useRef(null);
    const noise3D = useMemo(() => createNoise3D(), []);
    const mousePos = useRef({ x: 9999, y: 9999 });
    const startTime = useRef(Date.now());
    const { theme } = useTheme();

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        let canvasWidth, canvasHeight;
        let points = [];

        const project = (x, y, z) => {
            const rotatedY = y * Math.cos(config.rotationX) - z * Math.sin(config.rotationX);
            const rotatedZ = y * Math.sin(config.rotationX) + z * Math.cos(config.rotationX) + config.perspective;
            const scale = config.perspective / rotatedZ;
            return {
                x: x * scale,
                y: rotatedY * scale,
                scale,
                depth: rotatedZ,
            };
        };

        const resizeCanvas = () => {
            canvasWidth = canvas.parentElement.clientWidth;
            canvasHeight = canvas.parentElement.clientHeight;
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;

            const gridCols = Math.floor(canvasWidth / TILE_WIDTH);
            const gridRows = Math.floor((canvasHeight - NAVBAR_HEIGHT) / TILE_HEIGHT);

            points = [];
            for (let row = 0; row < gridRows; row++) {
                for (let col = 0; col < gridCols; col++) {
                    points.push({
                        x: (col - (gridCols - 1) / 2) * TILE_WIDTH,
                        y: (row - (gridRows - 1) / 2) * TILE_HEIGHT,
                        z: 0,
                        displayZ: 0,
                        col,
                        row,
                    });
                }
            }
        };
        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);

        let time = 0;
        let zoom = 1;
        const targetZoom = 2;

        const render = () => {
            time += 0.001;
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);

            if (zoom < targetZoom) {
                zoom += (targetZoom - zoom) * 0.02;
            }

            const particleColor = theme === "dark" ? "#eee" : "#333";

            points.forEach((point) => {
                const noiseVal = fbm3D(point.col * 0.1, point.row * 0.1, time, noise3D);
                const noiseZ = ((noiseVal + 1) / 2) * 120;

                let mouseZ = 0;
                if (config.enablePerspective) {
                    const pBase = project(point.x, point.y, 0);
                    const screenX = pBase.x * zoom + canvasWidth / 2;
                    const screenY = pBase.y * zoom + canvasHeight / 2;
                    const dist = Math.hypot(screenX - mousePos.current.x, screenY - mousePos.current.y);
                    const maxDist = 200;
                    if (dist < maxDist) {
                        mouseZ = (1 - dist / maxDist) * 150;
                    }
                }

                const targetZ = noiseZ - mouseZ;
                point.displayZ += (targetZ - point.displayZ) * 0.1;
            });

            if (config.enablePerspective) {
                points.sort((a, b) => {
                    const pA = project(a.x, a.y, a.displayZ);
                    const pB = project(b.x, b.y, b.displayZ);
                    return pB.depth - pA.depth;
                });
            }

            ctx.save();
            ctx.translate(canvasWidth / 2, canvasHeight / 2);
            ctx.scale(zoom, zoom);

            points.forEach((point) => {
                if (config.enablePerspective) {
                    const p = project(point.x, point.y, point.displayZ);
                    if (p.depth < 0) return;

                    const radius = 3 * p.scale;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
                    ctx.fillStyle = particleColor;
                    ctx.shadowColor = particleColor;
                    ctx.shadowBlur = 10 * p.scale;
                    ctx.fill();
                } else {
                    const radius = 3;
                    ctx.beginPath();
                    ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
                    ctx.fillStyle = particleColor;
                    ctx.shadowColor = particleColor;
                    ctx.shadowBlur = 10;
                    ctx.fill();
                }
            });

            ctx.restore();

            animationFrameId.current = requestAnimationFrame(render);
        };

        render();

        return () => {
            window.removeEventListener("resize", resizeCanvas);
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, [noise3D, theme]);

    useEffect(() => {
        const initializeNoiseCanvas = async () => {
            const noiseCanvas = noiseCanvasRef.current;
            const gl = noiseCanvas.getContext("webgl2");

            if (!gl) {
                console.error("WebGL2 not supported");
                return;
            }

            try {
                const vertexShaderSource = await loadShader("/shaders/vertex.glsl");
                const fragmentShaderSource = await loadShader("/shaders/fragment.glsl");

                const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
                const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);

                if (!vertexShader || !fragmentShader) {
                    console.error("Shader compilation failed. See details above.");
                    return;
                }

                const program = createProgram(gl, vertexShader, fragmentShader);
                if (!program) {
                    console.error("Shader program linking failed. See details above.");
                    return;
                }

                const positionAttributeLocation = gl.getAttribLocation(program, "a_position");
                const timeUniformLocation = gl.getUniformLocation(program, "u_time");
                const strengthUniformLocation = gl.getUniformLocation(program, "u_grain_strength");
                const resolutionUniformLocation = gl.getUniformLocation(program, "u_resolution");

                const positionBuffer = gl.createBuffer();
                gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);

                gl.useProgram(program);
                gl.enableVertexAttribArray(positionAttributeLocation);
                gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
                gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

                gl.enable(gl.BLEND);
                gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

                const resizeNoiseCanvas = () => {
                    const container = noiseCanvas.parentElement;
                    const dpr = window.devicePixelRatio || 1;
                    noiseCanvas.width = container.clientWidth * dpr;
                    noiseCanvas.height = container.clientHeight * dpr;
                    gl.viewport(0, 0, noiseCanvas.width, noiseCanvas.height);
                };

                resizeNoiseCanvas();
                window.addEventListener("resize", resizeNoiseCanvas);

                const renderNoise = () => {
                    gl.useProgram(program);
                    const elapsedTime = (Date.now() - startTime.current) / 1000;
                    gl.uniform1f(timeUniformLocation, elapsedTime);
                    gl.uniform1f(strengthUniformLocation, config.grainStrength);
                    gl.uniform2f(resolutionUniformLocation, noiseCanvas.width, noiseCanvas.height);

                    gl.clearColor(0, 0, 0, 0);
                    gl.clear(gl.COLOR_BUFFER_BIT);

                    gl.drawArrays(gl.TRIANGLES, 0, 6);

                    noiseAnimationFrameId.current = requestAnimationFrame(renderNoise);
                };

                renderNoise();

                return () => {
                    window.removeEventListener("resize", resizeNoiseCanvas);
                    if (noiseAnimationFrameId.current) {
                        cancelAnimationFrame(noiseAnimationFrameId.current);
                    }
                };
            } catch (error) {
                console.error("Error in initializeNoiseCanvas:", error);
            }
        };

        initializeNoiseCanvas();

        return () => {
            if (noiseAnimationFrameId.current) {
                cancelAnimationFrame(noiseAnimationFrameId.current);
            }
        };
    }, []);

    return (
        <div className="hero-container">
            <div className="canvas-container">
                <canvas ref={canvasRef} className="balls-canvas" />
                <canvas ref={noiseCanvasRef} className="noise-canvas" />
                {children}
            </div>
        </div>
    );
}
